import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getRevenueCatProductById } from '../graphql/queries';
import { updateRevenueCatProduct, deleteRevenueCatProduct } from '../graphql/mutation';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const PRODUCT_TYPES = [
  'WEEKLY',
  'MONTHLY', 
  'YEARLY',
  'INSTANT',
  'ONE_TIME',
  'PROMO'
];

export default function RevenueCatProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    startDate: '',
    endDate: '',
    limit: '',
    price: '',
    type: ''
  });

  const [originalData, setOriginalData] = useState(null);

  const { data, loading, error } = useQuery(getRevenueCatProductById, {
    variables: { id },
    skip: !id
  });

  const [updateProduct] = useMutation(updateRevenueCatProduct);
  const [deleteProduct] = useMutation(deleteRevenueCatProduct);

  // Set editing mode based on query parameter
  useEffect(() => {
    const editMode = searchParams.get('edit') === 'true';
    setIsEditing(editMode);
  }, [searchParams]);

  useEffect(() => {
    if (data?.getCreditProductById) {
      const product = data.getCreditProductById;
      setOriginalData(product);
      setFormData({
        name: product.name || '',
        amount: product.amount || '',
        startDate: product.startDate ? new Date(product.startDate).toISOString().slice(0, 16) : '',
        endDate: product.endDate ? new Date(product.endDate).toISOString().slice(0, 16) : '',
        limit: product.limit || '',
        price: product.price || '',
        type: product.type || ''
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || '' : value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({
      name: originalData.name || '',
      amount: originalData.amount || '',
      startDate: originalData.startDate ? new Date(originalData.startDate).toISOString().slice(0, 16) : '',
      endDate: originalData.endDate ? new Date(originalData.endDate).toISOString().slice(0, 16) : '',
      limit: originalData.limit || '',
      price: originalData.price || '',
      type: originalData.type || ''
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name || !formData.amount || !formData.startDate || !formData.endDate || !formData.limit || !formData.price || !formData.type) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error'
      });
      return;
    }

    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setSnackbar({
        open: true,
        message: 'Start date must be before end date',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await updateProduct({
        variables: {
          id,
          input: {
            name: formData.name,
            amount: parseInt(formData.amount),
            startDate: new Date(formData.startDate),
            endDate: new Date(formData.endDate),
            limit: parseInt(formData.limit),
            price: parseFloat(formData.price),
            type: formData.type
          }
        }
      });

      if (response.data) {
        setSnackbar({
          open: true,
          message: 'Product updated successfully!',
          severity: 'success'
        });
        setIsEditing(false);
        // Refresh data
        window.location.reload();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update product',
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProduct({
        variables: { id }
      });
      
      setSnackbar({
        open: true,
        message: 'Product deleted successfully!',
        severity: 'success'
      });
      
      setTimeout(() => {
        navigate('/revenuecat-products');
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete product',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTypeColor = (type) => {
    const colors = {
      WEEKLY: 'primary',
      MONTHLY: 'secondary',
      YEARLY: 'success',
      TIER1: 'info',
      TIER2: 'warning',
      TIER3: 'error',
      ONE_TIME: 'default'
    };
    return colors[type] || 'default';
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>Error: {error.message}</Box>;
  if (!data?.getCreditProductById) return <Box sx={{ p: 4, textAlign: 'center' }}>Product not found</Box>;

  const product = data.getCreditProductById;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/revenuecat-products')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            {isEditing ? 'Edit' : 'View'} RevenueCat Product
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isEditing ? (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Product Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Product Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
            >
              {PRODUCT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Credit Amount"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price ($)"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Daily Limit"
              name="limit"
              type="number"
              value={formData.limit}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              name="startDate"
              type="datetime-local"
              value={formData.startDate}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="End Date"
              name="endDate"
              type="datetime-local"
              value={formData.endDate}
              onChange={handleChange}
              disabled={!isEditing}
              variant={isEditing ? "outlined" : "filled"}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {!isEditing && (
          <>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created At
                </Typography>
                <Typography variant="body1">
                  {new Date(product.createdAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {new Date(product.updatedAt).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{product.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
